import { DealershipHours, DayHours } from '../types';

// Default hours: Monday-Saturday 10AM-7PM, Sunday closed
const DEFAULT_HOURS: DealershipHours = {
  monday: { start: '10:00', end: '19:00', isClosed: false },
  tuesday: { start: '10:00', end: '19:00', isClosed: false },
  wednesday: { start: '10:00', end: '19:00', isClosed: false },
  thursday: { start: '10:00', end: '19:00', isClosed: false },
  friday: { start: '10:00', end: '19:00', isClosed: false },
  saturday: { start: '10:00', end: '19:00', isClosed: false },
  sunday: { start: '', end: '', isClosed: true },
};

/**
 * Parses dealership hours JSON string or returns defaults if null/invalid
 * @param hoursJson - JSON string from dealer_settings.dealershipHours column
 * @returns Parsed DealershipHours object
 */
export function parseDealershipHours(hoursJson: string | null | undefined): DealershipHours {
  // If null or empty, return defaults
  if (!hoursJson || hoursJson.trim() === '') {
    return DEFAULT_HOURS;
  }

  try {
    const parsed = JSON.parse(hoursJson);

    // Validate that all days exist, otherwise use defaults
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    for (const day of daysOfWeek) {
      if (!parsed[day] || typeof parsed[day] !== 'object') {
        console.warn(`[Dealership Hours] Missing or invalid data for ${day}, using defaults`);
        return DEFAULT_HOURS;
      }
    }

    return parsed as DealershipHours;
  } catch (error) {
    console.error('[Dealership Hours] Failed to parse dealership hours JSON:', error);
    return DEFAULT_HOURS;
  }
}

/**
 * Gets the hours for a specific day of the week
 * @param dealershipHours - Dealership hours object
 * @param date - Date to get hours for
 * @returns DayHours for the given date
 */
export function getHoursForDate(dealershipHours: DealershipHours, date: Date): DayHours {
  const dayIndex = date.getDay();
  const dayNames: (keyof DealershipHours)[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  return dealershipHours[dayNames[dayIndex]];
}

/**
 * Converts time string (HH:mm) to hour number
 * @param timeString - Time in format "HH:mm" (e.g., "10:00")
 * @returns Hour as number (e.g., 10)
 */
export function parseTimeToHour(timeString: string): number {
  const [hours] = timeString.split(':');
  return parseInt(hours, 10);
}

/**
 * Generates available hours for a specific date based on dealership hours
 * @param dealershipHours - Dealership hours object
 * @param date - Date to generate hours for
 * @returns Array of available hour numbers (e.g., [10, 11, 12, ...])
 */
export function getAvailableHoursForDate(
  dealershipHours: DealershipHours,
  date: Date
): number[] {
  const dayHours = getHoursForDate(dealershipHours, date);

  // If the day is closed or has invalid hours, return empty array
  if (dayHours.isClosed || !dayHours.start || !dayHours.end) {
    return [];
  }

  const startHour = parseTimeToHour(dayHours.start);
  const endHour = parseTimeToHour(dayHours.end);

  // Generate array of hours [startHour, startHour+1, ..., endHour-1]
  const hours: number[] = [];
  for (let hour = startHour; hour < endHour; hour++) {
    hours.push(hour);
  }

  return hours;
}

/**
 * Checks if dealership is open on a specific date
 * @param dealershipHours - Dealership hours object
 * @param date - Date to check
 * @returns true if open, false if closed
 */
export function isDealershipOpen(dealershipHours: DealershipHours, date: Date): boolean {
  const dayHours = getHoursForDate(dealershipHours, date);
  return !dayHours.isClosed && !!dayHours.start && !!dayHours.end;
}
